import { MenuItemInfo } from '@tableau/widgets';
import React from 'react';

import { getAppStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../codegen/Localize';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { DomainFamily } from '../../settings/types/IServerSettingsUnauthenticated';
import { hasSiteLicensingConsumptionInfo } from '../../ts/services/LicensesService';
import { ActiveDirectoryGroupAction } from '../groups/services/actions/ActiveDirectoryGroupAction';
import { AddActiveDirectoryUsersAction } from '../services/AddActiveDirectoryUsersAction';
import { AddExternalUsersAction } from '../services/AddExternalUsersAction';
import { AddLocalUserAction, AddLocalUserDialogType } from '../services/AddLocalUserAction';
import { BulkImportUsersAction } from '../services/BulkImportUsersAction';
import { SiteUsers } from '../services/resources/SiteUsers';
import { AddContentButton } from './AddContentButton';

export interface AddUsersButtonProps {
  readonly isServerUsersSpace: boolean;
  readonly requestSpaceContents?: () => void;
  readonly addButtonText: string;
  readonly buyLicense?: () => void;
  readonly showBuyLicensesOption?: boolean;
  readonly showSelectExistingUsersOption?: boolean;
}

export enum AddUserDialogType {
  AddLocalUser = 'addLocalUser',
  AddADUser = 'addADUser',
  AddExternalUser = 'addExternalUser',
  BulkImportUsers = 'bulkImportUsers',
  ImportADGroup = 'importADGroup',
  BuyLicenses = 'buyLicenses',
  SelectExistingUsers = 'selectingExistingUsers'
}

const ADD_USERS_TEST_ID = 'action-add-users';
const ADD_USERS_OR_LICENSE_TEST_ID = 'action-add-users-or-licenses';

export class AddUsersButton extends React.Component<AddUsersButtonProps> {
  private domainFamily: string = ServerSettingsUnauthenticated.domainFamily();
  private modalInstancePromise: Promise<any>;

  public constructor(props: AddUsersButtonProps) {
    super(props);
  }

  public createAddServerUserMenuItemOptions = (): MenuItemInfo[] => {
    let menuItemInfos: MenuItemInfo[] = [];

    if (this.domainFamily === DomainFamily.Local) {
      menuItemInfos.push({displayValue: Messages.addUsersLocalButton(), value: AddUserDialogType.AddLocalUser});
    } else if (this.domainFamily === DomainFamily.ActiveDirectory) {
      menuItemInfos.push({displayValue: Messages.addUsersImportAdButton(), value: AddUserDialogType.AddADUser});
    }

    menuItemInfos.push({displayValue: Messages.addUsersBulkImportButton(), value: AddUserDialogType.BulkImportUsers});
    return menuItemInfos;
  }

  public createAddSiteUserMenuItemOptions = (): MenuItemInfo[] => {
    let menuItemInfos: MenuItemInfo[] = [];

    if (this.domainFamily === DomainFamily.Local) {
      menuItemInfos.push({displayValue: Messages.addUsersLocalButton(), value: AddUserDialogType.AddLocalUser});
    } else if (this.domainFamily === DomainFamily.ActiveDirectory) {
      menuItemInfos.push({displayValue: Messages.addUsersImportAdButton(), value: AddUserDialogType.AddADUser});
      menuItemInfos.push({displayValue: Messages.addUsersImportAdGroupButton(), value: AddUserDialogType.ImportADGroup});
    } else if (this.domainFamily === DomainFamily.External) {
      menuItemInfos.push({displayValue: Messages.addUsersImportExternalButton(), value: AddUserDialogType.AddExternalUser});
    }

    menuItemInfos.push({displayValue: Messages.addUsersBulkImportButton(), value: AddUserDialogType.BulkImportUsers});

    if (this.props.showBuyLicensesOption) {
      menuItemInfos.push({isDivider: true});
      menuItemInfos.push({displayValue: Messages.buyLicenses(), value: AddUserDialogType.BuyLicenses});
    }

    if (this.props.showSelectExistingUsersOption) {
      menuItemInfos.push({isDivider: true});
      menuItemInfos.push({displayValue: Messages.siteAuthenticationViewUsers(), value: AddUserDialogType.SelectExistingUsers});
    }

    return menuItemInfos;
  }

  public getAvailableQuota() {
    return hasSiteLicensingConsumptionInfo().then((hasCapacity: boolean) => {
      if (!hasCapacity) {
        return SiteUsers.instance.getRemainingUserQuota();
      } else {
        return undefined;
      }
    });
  }

  public onSelectUserOption = async (addUserDialogType: AddUserDialogType): Promise<void> => {
    switch(addUserDialogType) {
      case AddUserDialogType.AddLocalUser:
      {
        let addLocalUserDialogType = this.props.isServerUsersSpace ? AddLocalUserDialogType.SERVER : AddLocalUserDialogType.SITE;
        let availableQuota = this.props.isServerUsersSpace ? undefined : await this.getAvailableQuota();
        this.modalInstancePromise = AddLocalUserAction.instance.showAddLocalUserDialog(addLocalUserDialogType, availableQuota);
        break;
      }
      case AddUserDialogType.AddADUser:
      {
        let availableQuota = this.props.isServerUsersSpace ? undefined : await this.getAvailableQuota();
        this.modalInstancePromise = this.props.isServerUsersSpace ?
          AddActiveDirectoryUsersAction.showAddActiveDirectoryUsersToServerDialog() :
          AddActiveDirectoryUsersAction.showAddActiveDirectoryUsersToSiteDialog(availableQuota);
        break;
      }
      case AddUserDialogType.AddExternalUser:
      {
        let availableQuota = await this.getAvailableQuota();
        this.modalInstancePromise = AddExternalUsersAction.showAddExternalUsersToSiteDialog(availableQuota);
        break;
      }
      case AddUserDialogType.BulkImportUsers:
        this.modalInstancePromise = this.props.isServerUsersSpace ?
          BulkImportUsersAction.showBulkImportServerUsersDialog() :
          BulkImportUsersAction.showBulkImportSiteUsersDialog();
        break;
      case AddUserDialogType.ImportADGroup:
        this.modalInstancePromise = ActiveDirectoryGroupAction.showAddAdGroupDialog();
        break;
      case AddUserDialogType.BuyLicenses:
        this.props.buyLicense();
        return;
      case AddUserDialogType.SelectExistingUsers:
        window.location.href = siteUrlsSelector(getAppStore().getState()).usersUrl();
        return;
      default:
        return;
    }

    this.modalInstancePromise.then(() => {
      this.props.requestSpaceContents();
    });
  }

  public render(): JSX.Element {
    return (
      <AddContentButton
        buttonText={this.props.addButtonText}
        testId={this.props.showBuyLicensesOption ? ADD_USERS_OR_LICENSE_TEST_ID : ADD_USERS_TEST_ID}
        menuItems={this.props.isServerUsersSpace ? this.createAddServerUserMenuItemOptions() :
          this.createAddSiteUserMenuItemOptions()}
        onSelect={this.onSelectUserOption}
      />
    );
  }
}

